/**
 * German QWERTZ (Windows KBDGR / Unicode CLDR `de-t-k0-windows`): maps a physical code to the
 * unshifted character that key emits. Letters are uppercase by table convention; accented chars
 * (a-umlaut, o-umlaut, u-umlaut, eszett) stay in their unshifted lowercase form. Y and Z are
 * swapped from US Qwerty (the defining QWERTZ trait). Backquote (circumflex) and Equal (acute)
 * are dead keys on a real board; here they are legends only. The ISO key left of Z (IntlBackslash,
 * "less-than/greater-than") is outside the US-Qwerty code block and is not bound by TheCore.
 */
export const QWERTZ_CODE_TO_CHAR: Record<string, string> = {
  Backquote: "^", // dead circumflex
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
  Digit9: "9",
  Digit0: "0",
  Minus: "ß", // eszett
  Equal: "´", // dead acute (U+00B4)
  KeyQ: "Q",
  KeyW: "W",
  KeyE: "E",
  KeyR: "R",
  KeyT: "T",
  KeyY: "Z", // Z/Y swap
  KeyU: "U",
  KeyI: "I",
  KeyO: "O",
  KeyP: "P",
  BracketLeft: "ü", // u umlaut
  BracketRight: "+",
  Backslash: "#",
  KeyA: "A",
  KeyS: "S",
  KeyD: "D",
  KeyF: "F",
  KeyG: "G",
  KeyH: "H",
  KeyJ: "J",
  KeyK: "K",
  KeyL: "L",
  Semicolon: "ö", // o umlaut
  Quote: "ä", // a umlaut
  KeyZ: "Y", // Z/Y swap
  KeyX: "X",
  KeyC: "C",
  KeyV: "V",
  KeyB: "B",
  KeyN: "N",
  KeyM: "M",
  Comma: ",",
  Period: ".",
  Slash: "-",
};

/**
 * The .SC2Hotkeys serialization token per code where QWERTZ's token diverges from charToken of its
 * character. SC2 binds these OEM keys by virtual-key, so the file names the
 * key by its German VK, not the umlaut/symbol it emits: the eszett key serializes as "BracketOpen"
 * (VK_OEM_4), the a-umlaut key as "Apostrophe" (VK_OEM_7), and so on. Recovered from TheCore's real
 * US-Qwerty -> Germany pair (both hands) and verified to reproduce those files command-for-command.
 * Letters (including the Y/Z swap) and digits are omitted: a letter matches charToken(map[code]) via
 * the char path, and a digit token is positional. The map is injective so every key round-trips.
 * Backquote's token is witnessed only in the left-hand file; the right-hand file leaves its one
 * Backquote binding (ChatAllies) on "Grave" unconverted - a documented TheCore source quirk.
 */
export const QWERTZ_CODE_TO_TOKEN: Record<string, string> = {
  Backquote: "BackSlash", // ^ dead key, VK_OEM_5
  Minus: "BracketOpen", // ß, VK_OEM_4
  Equal: "BracketClose", // ´ dead key, VK_OEM_6
  BracketLeft: "SemiColon", // ü, VK_OEM_1
  BracketRight: "Equals", // +, VK_OEM_PLUS
  Backslash: "Slash", // #, VK_OEM_2
  Semicolon: "Grave", // ö, VK_OEM_3
  Quote: "Apostrophe", // ä, VK_OEM_7
};
